import { useController, type Control, type FieldPath, type FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Select, { type MultiValue, type SingleValue } from "react-select";

interface SelectOption {
  value: string;
  label: string;
}

interface SelectInputProps<TFieldValues extends FieldValues = FieldValues> {
  /** The field name in the form */
  name: FieldPath<TFieldValues>;
  /** React Hook Form control object */
  control: Control<TFieldValues>;
  /** Optional label for the select */
  label?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Array of select options */
  options?: SelectOption[];
  /** Validation rules for the field */
  rules?: Parameters<typeof useController<TFieldValues>>[0]["rules"];
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Enable multi-select mode */
  isMulti?: boolean;
  /** Enable search/filter */
  isSearchable?: boolean;
  /** Disable specific options */
  isOptionDisabled?: (option: SelectOption) => boolean;
}

export default function SelectInput<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  label,
  placeholder,
  options = [],
  rules,
  disabled,
  className = "",
  isMulti = false,
  isSearchable = true,
  isOptionDisabled,
}: SelectInputProps<TFieldValues>) {
  const { t } = useTranslation();
  const {
    field: { value, onChange, onBlur, ref },
    fieldState: { error },
  } = useController<TFieldValues>({
    name,
    control,
    rules,
  });

  // Convert form value to react-select format
  const selectValue = isMulti
    ? options.filter((option) => value?.includes(option.value))
    : options.find((option) => option.value === value) || null;

  // Handle change to convert back to form format
  const handleChange = (newValue: MultiValue<SelectOption> | SingleValue<SelectOption>) => {
    if (isMulti) {
      const multiValue = newValue as MultiValue<SelectOption>;
      onChange(multiValue ? Array.from(multiValue).map((item) => item.value) : []);
    } else {
      const singleValue = newValue as SingleValue<SelectOption>;
      onChange(singleValue ? singleValue.value : "");
    }
  };

  return (
    <div className={className}>
      {label && (
        <label htmlFor={name} className="form-label">
          {label}
        </label>
      )}
      <Select
        inputId={name}
        ref={ref}
        value={selectValue}
        onChange={handleChange}
        onBlur={onBlur}
        options={options}
        isMulti={isMulti}
        isSearchable={isSearchable}
        isDisabled={disabled}
        isOptionDisabled={isOptionDisabled}
        placeholder={placeholder}
        isClearable
        classNamePrefix="react-select"
        styles={{
          control: (base) => ({
            ...base,
            minHeight: '42px',
            borderRadius: '5px',
            borderColor: error ? "#dc3545" : base.borderColor,
            "&:hover": {
              borderColor: error ? "#dc3545" : base.borderColor,
            },
          }),
          menu: (base) => ({
            ...base,
            zIndex: 9999,
          }),
        }}
      />
      {error && (
        <div className="invalid-feedback d-block">
          {error.message ? t(error.message) : ""}
        </div>
      )}
    </div>
  );
}

